import { Chart as ChartJS } from 'chart.js'
import { FC, useEffect, useRef } from 'react'
import { Line } from 'react-chartjs-2'

import { Box, useTheme } from '@mui/material'

import { useMotivationRows } from '../../hooks/useMotivationRows'
import { SecondData } from '../../types'
import { TimelineLegend } from './TimelineLegend'
import { PX_PER_SECOND, ROW_BOTTOM_PAD, ROW_H, ROW_TOP_PAD, TIME_AXIS_H, VIEWPORT_INSET } from './TimelinePalette'
import { TimelineTooltip } from './TimelineTooltip'
import { useTimelineChartData } from './useTimelineChartData'
import { useTimelineTooltip } from './useTimelineTooltip'

interface MotivationTimelineProps {
  seconds: SecondData[]
  currentSecond?: number | null
  onSecondClick?: (second: number) => void
}

/**
 * Per-second motivation intensity chart. The motivation rows are stacked on a
 * single Chart.js linear scale, with hover/click handled by an outer wrapper
 * (so events fire over the x-axis label strip too) and a portal-rendered
 * tooltip that pins to the chart rows.
 *
 * This file is intentionally a thin renderer: chart datasets/options live in
 * {@link useTimelineChartData}, tooltip state + pointer wiring live in
 * {@link useTimelineTooltip}, the floating tooltip is {@link TimelineTooltip},
 * and the sticky label column is {@link TimelineLegend}.
 */
export const MotivationTimeline: FC<MotivationTimelineProps> = ({ seconds, currentSecond, onSecondClick }) => {
  const theme = useTheme()
  const scrollRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<ChartJS<'line'>>(null)
  const { motivationKeysInOrder, motivationKeyToLabel } = useMotivationRows(seconds)

  const numRows = motivationKeysInOrder.length
  const seriesWidth = Math.max(seconds.length * PX_PER_SECOND, 200)
  const chartHeight = numRows * ROW_H + TIME_AXIS_H + ROW_TOP_PAD + ROW_BOTTOM_PAD

  const { tooltip, tooltipPosition, hoverSecond, handleWrapperMouseMove, handleWrapperClick, handleWrapperMouseLeave } = useTimelineTooltip({
    seconds,
    numRows,
    chartRef,
    onSecondClick,
    motivationKeysInOrder,
  })

  const { data, options } = useTimelineChartData({ seconds, currentSecond, hoverSecond, theme, numRows, motivationKeysInOrder, motivationKeyToLabel })

  // Auto-scroll the active second into view whenever it changes off-screen.
  useEffect(() => {
    const chart = chartRef.current
    const container = scrollRef.current
    if (!chart || !container || currentSecond == null) return
    const px = chart.scales.x.getPixelForValue(currentSecond)
    if (px == null) return
    const visibleStart = container.scrollLeft
    const visibleEnd = visibleStart + container.clientWidth
    if (px < visibleStart + VIEWPORT_INSET) {
      container.scrollTo({ left: Math.max(0, px - VIEWPORT_INSET), behavior: 'smooth' })
    } else if (px > visibleEnd - VIEWPORT_INSET) {
      container.scrollTo({ left: px - container.clientWidth + VIEWPORT_INSET, behavior: 'smooth' })
    }
  }, [currentSecond])

  if (seconds.length === 0) return null

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
      <TimelineLegend motivationKeysInOrder={motivationKeysInOrder} />
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'action.disabled', borderRadius: 3 },
        }}
      >
        <Box
          onMouseMove={handleWrapperMouseMove}
          onMouseLeave={handleWrapperMouseLeave}
          onClick={handleWrapperClick}
          sx={{
            width: seriesWidth,
            height: chartHeight,
            position: 'relative',
            cursor: onSecondClick ? 'pointer' : 'default',
          }}
        >
          <Line ref={chartRef} data={data} options={options} />
          {tooltip && tooltipPosition && <TimelineTooltip tooltip={tooltip} position={tooltipPosition} />}
        </Box>
      </Box>
    </Box>
  )
}
